import { Member } from 'modules/stream/domain/interfaces/game-info/gameInfoMapped';
import { selectActiveBoard } from 'modules/template-editor/store/templates/selectors';
import { setActiveBoard } from 'modules/template-editor/store/templates/templates';
import { MutableRefObject, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { capitalize } from 'shared/utils';
import { AppDispatch } from 'store/store';
import useGameInfo from '../../../../../stream/ui/hooks/use-game-info';
import Static from '../../canvases/static';
import useFade from '../use-fade';
import useReplacer from '../use-replacer';
import getTeamPositions from './helpers/get-team-positions';
import useOnLoadedCommon from './use-on-loaded-common';

interface IProps {
  manager: Static;
  timeout: MutableRefObject<NodeJS.Timer | null>;
}

function useOnLoadedCarousel(props: IProps) {
  const { manager, timeout } = props;
  const dispatch = useDispatch<AppDispatch>();
  const playerIndexRef = useRef(0);
  const maxPlayerIndexRef = useRef(0);
  const { t } = useTranslation();
  const gameInfo = useGameInfo();
  const replacer = useReplacer(manager);
  const fade = useFade(manager);
  const activeBoard = useSelector(selectActiveBoard);
  const { onLoaded: onLoadedCommon } = useOnLoadedCommon({ manager });

  const handleResetActiveBoard = useCallback(() => {
    dispatch(setActiveBoard({ id: undefined }));
  }, [dispatch]);

  // Плоские списки игроков команд, упорядоченные по позициям. Пара формируется по
  // сквозному индексу, поэтому при равном числе игроков пропусков (одиночных пар) нет.
  const firstTeamPlayers: Member[] = useMemo(() => {
    return flattenByPosition(gameInfo.teams?.first.members ?? []);
  }, [gameInfo.teams?.first.members]);
  const secondTeamPlayers: Member[] = useMemo(() => {
    return flattenByPosition(gameInfo.teams?.second.members ?? []);
  }, [gameInfo.teams?.second.members]);

  const replacePlayer = useCallback(
    (teamPlayers: Member[], teamIndex: number) => {
      const player = (teamPlayers[playerIndexRef.current] ?? null) as Member | null;
      const positionName = player?.position && t(`player.position.${player?.position}`);
      // Нет фото игрока — подставляем логотип его команды, затем дефолтный аватар.
      const teamLogo = teamIndex === 0 ? gameInfo.teams?.first?.logo : gameInfo.teams?.second?.logo;
      replacer.carousel.replace({
        isFadeIn: true,
        teamIndex,
        startOpacity: 0,
        data: {
          avatar: player?.photo ?? teamLogo ?? USER_DEFAULT_AVATAR,
          name: `${player?.name ?? ''} ${player?.surname ?? ''}`,
          number: player?.number,
          position: positionName && capitalize(positionName),
        },
      });

      return player;
    },
    [gameInfo.teams, replacer.carousel, t],
  );

  function nextPlayer() {
    const allObjects = manager?.canvas?.getObjects() ?? [];
    const carouselObjects = allObjects?.filter((object) => object?.data?.indicator?.includes('carousel') || object?.data?.delete);
    if (carouselObjects?.length === 0) {
      return;
    }

    playerIndexRef.current += 1;
    if (playerIndexRef.current >= maxPlayerIndexRef.current) {
      fade.fadeOut(allObjects).then(handleResetActiveBoard);
      return;
    }

    fade.fadeOut(carouselObjects, { deleteAfter: false }).then(() => {
      for (const obj of carouselObjects) {
        if (obj?.data?.delete) {
          manager?.canvas?.remove(obj);
        }
      }
      loadCurrentPlayer();
    });
  }

  const loadCurrentPlayer = useCallback(() => {
    const first = replacePlayer(firstTeamPlayers, 0);
    const second = replacePlayer(secondTeamPlayers, 1);
    const isSkip = !first && !second;

    const allObject = manager?.canvas?.getObjects().filter((object) => object.opacity === 0) ?? [];
    for (const obj of allObject) {
      fade.fadeIn([obj]);
    }

    if (isSkip) {
      nextPlayer();
    } else {
      new Promise((resolve) => {
        timeout.current = setTimeout(resolve, (activeBoard?.duration ?? 5) * 1000);
      }).then(nextPlayer);
    }
  }, [activeBoard?.duration, fade, firstTeamPlayers, manager?.canvas, nextPlayer, replacePlayer, secondTeamPlayers, timeout]);

  const onLoaded = useCallback(() => {
    if (!manager) {
      return;
    }
    if (!gameInfo) {
      return;
    }
    if (!activeBoard) return;
    replacer.utils.ungroupObjects();

    onLoadedCommon();

    playerIndexRef.current = 0;
    maxPlayerIndexRef.current = Math.max(firstTeamPlayers.length, secondTeamPlayers.length);

    loadCurrentPlayer();
  }, [activeBoard, firstTeamPlayers, gameInfo, loadCurrentPlayer, manager, onLoadedCommon, replacer.utils, secondTeamPlayers]);

  return { onLoaded };
}

export default useOnLoadedCarousel;

const positions = ['goalkeeper', 'defender', 'midfielder', 'forward'];

// Разворачиваем игроков в один список в порядке позиций (getTeamPositions уже
// отфильтровал по isInvolved и сгруппировал по позиции).
function flattenByPosition(teamMembers: Member[]): Member[] {
  const byPosition = getTeamPositions({ teamMembers });
  return positions.flatMap((position) => byPosition[position] ?? []);
}
